import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { MemberDetailPanel } from '@components/MemberDetailPanel';
import {
  BrowseToolbar, SpacerRow, useBrowseVirtual,
  type BrowseItem,
} from '@components/lookup/browse-kit';
import { GridFrame, GridHeadRow, GridToolbar, gridCell } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { multiSort } from '@lib/grid';
import { SlicerRail } from '@components/lookup/SlicerRail';
import { memberDetail, shippingDetail, shippingList, shippingListExport, shippingRowKey } from '@lib/care-api';
import { hayIncludes } from '@lib/hay';
import {
  EMPTY_LOOKUP_FILTERS, activeFilterCount, buildShippingAxes, matchShippingOps, type LookupFilters,
} from '@lib/lookup-axes';
import { fmtMemberId } from '@lib/member-id';
import type { MemberDetailResp, ShippingListResp, ShippingRow, ShippingScan, EmptyReason } from '@lib/care-model';
import { EMPTY_REASON_META, emptyTone, emptyRow } from '@lib/care-model';
import { isValidReason, useSessionReason } from '@lib/useSessionReason';

/**
 * ShippingLookupSection - the former Shipping tab as a browse-all table (2026-07-14 rework, Portal
 * Enroute parity): EVERY kit + mailed letter loads once behind the session reason (newest first),
 * searched + sorted client-side, virtualized. A kit row expands into its USPS scan timeline and the
 * member's full record (reason-audited, unchanged); a letter row expands into its letter fields + the
 * record.
 *
 * ROW 418 (William, 19 Sep 2026): the Shipping tab is FOLDED INTO ENROUTE. Its kits half is now the
 * Enroute tab's kits view (EnrouteSection, the Portal's columns); this section survives as Enroute's
 * LETTERS sub-tab (`only="letter"`, the Portal's Kits / Letters toggle), because it holds the mailed
 * result letters the kits view does not. It gained the Portal's habits: the export of exactly the
 * rows on screen behind the page's export-scoped re-prompt, and the search and filter beacons.
 */

/** One loaded row + its load-time stable uid (sorting/filtering must not re-key rows). */
interface Row {
  uid: string;
  r: ShippingRow;
}

type ColKey = 'type' | 'member' | 'ref' | 'shipped' | 'outbound' | 'return' | 'received';

/**
 * Sortable date key across the mixed formats the campaign DB carries (kit ship_date 'MM/DD/YYYY',
 * letter sent_date ISO) - the API-side dateKey mirrored so the Shipped/sent sort interleaves kinds
 * correctly instead of grouping letters above kits.
 */
function dateKey(v: string): string {
  const s = (v ?? '').trim();
  const us = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);
  return us ? `${us[3]}-${us[1]}-${us[2]}` : s;
}

function sortValue(x: Row, key: ColKey): string | number {
  const r = x.r;
  switch (key) {
    case 'type': return r.kind;
    case 'member': return (r.full_name || '').toLowerCase();
    case 'ref': return r.kind === 'kit' ? r.barcode_id : r.letter_id || r.tracking_code;
    case 'shipped': return dateKey(r.kind === 'kit' ? r.ship_date : r.sent_date);
    case 'outbound': return r.kind === 'kit' ? r.outbound_status || r.outbound_tracking : r.delivery_status || r.tracking_code;
    case 'return': return r.kind === 'kit' ? r.return_status || r.return_tracking : '';
    case 'received': return r.kind === 'kit' ? r.sample_received_ts || r.delivered_to_member_ts : r.delivered_ts;
  }
}

const dash = <span className="muted">-</span>;

function cell(v: string) {
  return v || dash;
}

/** Tracking + status stacked in one cell (the Portal Enroute column shape). */
function trackCell(tracking: string, status: string) {
  if (!tracking && !status) return <>{dash}</>;
  // 'Returned to Sender' is derived server-side (feedback #15: USPS's final scan on a bounced kit
  // says "Delivered, To Original Sender" and the upstream rollup called it Delivered). It must read
  // as an alert, never blend into the muted status line - these members still need a kit.
  const rts = /returned to sender/i.test(status);
  return (
    <>
      <div style={{ whiteSpace: 'nowrap' }}>{tracking || dash}</div>
      {status && (rts
        ? <div style={{ fontSize: '0.85em' }}><span className="pill pill-warning">{status}</span></div>
        : <div className="muted" style={{ fontSize: '0.85em' }}>{status}</div>)}
    </>
  );
}

export interface ShippingLookupSectionProps {
  /** Narrow the read to one kind (Enroute's Letters sub-tab passes 'letter'); absent = kits and letters. */
  only?: 'kit' | 'letter';
  onNeedReason?: () => void;
  onRequireExportReason?: (proceed: (reason: string) => void) => void;
  onAuditSearch?: (query: string) => void;
  onAuditFilter?: (filters: Record<string, string[]>) => void;
}

export function ShippingLookupSection({ only, onNeedReason, onRequireExportReason, onAuditSearch, onAuditFilter }: ShippingLookupSectionProps = {}) {
  const kind: 'all' | 'kit' | 'letter' = only ?? 'all';
  const noun = kind === 'letter' ? 'letters' : kind === 'kit' ? 'kits' : 'shipments';
  const sessionReason = useSessionReason();
  const reasonValid = isValidReason(sessionReason);

  const [rows, setRows] = useState<Row[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [available, setAvailable] = useState(true);
  const [emptyReason, setEmptyReason] = useState<EmptyReason | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loadedFor, setLoadedFor] = useState('');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState<LookupFilters>(EMPTY_LOOKUP_FILTERS);

  const [openUid, setOpenUid] = useState<string | null>(null);
  const [scans, setScans] = useState<Record<string, ShippingScan[] | 'none'>>({});
  const [memberDetails, setMemberDetails] = useState<Record<string, MemberDetailResp>>({});
  const [detailLoading, setDetailLoading] = useState('');
  const [note, setNote] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  // Load the whole shipping population once per valid session reason.
  useEffect(() => {
    if (!reasonValid || loadedFor === sessionReason) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    void shippingList(sessionReason, undefined, kind)
      .then((resp) => {
        if (cancelled) return;
        if (resp.ok) {
          const d = resp.data as ShippingListResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setRows(list.map((r, i) => ({
            uid: `${r.kind}|${r.kind === 'kit' ? r.barcode_id : `${r.letter_id}|${r.tracking_code}`}|${r.member_id}|${i}`,
            r,
          })));
          setTruncated(!!d.truncated);
          setAvailable(d.available !== false && (d.kitsAvailable || d.lettersAvailable));
          setEmptyReason(d.emptyReason);
          setLoadedFor(sessionReason);
        } else {
          setLoadErr(`Shipping load failed (HTTP ${resp.status}).`);
        }
        // The load is over on BOTH branches, set HERE rather than in a finally: a finally runs a
        // microtask after the state above, by which time React may have re-run this effect (loadedFor
        // changed) and flipped `cancelled`, leaving `loading` stuck true (found by the sections test).
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('Shipping load failed.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, loadedFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Row 418: the beacons, one per settled non-empty search and one per committed filter change.
  useEffect(() => {
    const q = deferredSearch.trim();
    if (q) onAuditSearch?.(q);
  }, [deferredSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      if (activeFilterCount(filters) === 0) return;
    }
    onAuditFilter?.(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rail filters + search + sort in memory: member id / name, kit barcode, letter id, tracking.
  const railFiltered = useMemo(
    () => (Object.keys(filters).length ? rows.filter((x) => matchShippingOps(x.r, filters)) : rows),
    [rows, filters],
  );

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return railFiltered;
    return railFiltered.filter(({ r }) => {
      const refs =
        r.kind === 'kit'
          ? `${r.barcode_id} ${r.outbound_tracking} ${r.return_tracking}`
          : `${r.letter_id} ${r.tracking_code}`;
      const hay = `${r.member_id} ${r.full_name} ${refs}`.toLowerCase();
      return hayIncludes(hay, q);
    });
  }, [railFiltered, deferredSearch]);

  const axes = useMemo(() => buildShippingAxes(rows.map((x) => x.r)), [rows]);

  /**
   * ROW 356: the shared table's habits on the shipping list (Columns, drag to resize, click and
   * Shift-click sorting over every loaded row, Expand), remembered per operator. The rail keeps
   * the slicing.
   */
  const nowrap = { whiteSpace: 'nowrap' } as const;
  const columns = useMemo<GridColumn<Row>[]>(() => [
    {
      key: 'type', label: 'Type', tdStyle: nowrap, sortValue: (x) => sortValue(x, 'type'),
      render: (x) => (
        <>
          {detailLoading === x.uid ? '... ' : openUid === x.uid ? '▾ ' : '▸ '}
          {x.r.kind === 'kit' ? 'Kit' : 'Letter'}
        </>
      ),
    },
    {
      key: 'member', label: 'Member', tdStyle: nowrap, sortValue: (x) => sortValue(x, 'member'),
      render: ({ r }) => (
        <>
          <b>{r.full_name || 'member'}</b>{' '}
          {r.member_id && <span className="muted small">#{fmtMemberId(r.member_id)}</span>}
        </>
      ),
    },
    {
      key: 'ref', label: 'Ref', tdClassName: 'small', tdStyle: nowrap, sortValue: (x) => sortValue(x, 'ref'),
      render: ({ r }) => (r.kind === 'kit' ? cell(r.barcode_id) : cell(r.letter_id || r.tracking_code)),
    },
    {
      key: 'shipped', label: 'Shipped / sent', tdClassName: 'small', tdStyle: nowrap, firstDir: 'desc', sortValue: (x) => sortValue(x, 'shipped'),
      render: ({ r }) => (r.kind === 'kit' ? cell(r.ship_date) : cell(r.sent_date)),
    },
    {
      key: 'outbound', label: 'Outbound', tdClassName: 'small', sortValue: (x) => sortValue(x, 'outbound'),
      render: ({ r }) =>
        r.kind === 'kit'
          ? trackCell(r.outbound_tracking, r.outbound_status)
          : trackCell(r.tracking_code, [r.carrier, r.delivery_status].filter(Boolean).join(' · ')),
    },
    {
      key: 'return', label: 'Return', tdClassName: 'small', sortValue: (x) => sortValue(x, 'return'),
      render: ({ r }) => (r.kind === 'kit' ? trackCell(r.return_tracking, r.return_status) : dash),
    },
    {
      key: 'received', label: 'Received / delivered', tdClassName: 'small', tdStyle: nowrap, firstDir: 'desc', sortValue: (x) => sortValue(x, 'received'),
      render: ({ r }) => (r.kind === 'kit' ? cell(r.sample_received_ts || r.delivered_to_member_ts) : cell(r.delivered_ts)),
    },
  ], [detailLoading, openUid]);
  const view = useGridView('lookup-shipping', columns, { defaultSort: [{ k: 'shipped', asc: false }] });
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const sorted = useMemo(
    () => multiSort(filtered, view.sort, (x, k) => byKey.get(k)?.sortValue?.(x)),
    [filtered, view.sort, byKey],
  );
  const span = Math.max(1, view.visible.length);

  const displayItems = useMemo<BrowseItem<Row>[]>(() => {
    const out: BrowseItem<Row>[] = [];
    for (const x of sorted) {
      out.push({ kind: 'row', row: x });
      if (x.uid === openUid) out.push({ kind: 'detail', row: x });
    }
    return out;
  }, [sorted, openUid]);
  const virtual = useBrowseVirtual(displayItems, (it) => `${it.kind}:${it.row.uid}`, 420);

  const toggleRow = async (x: Row) => {
    const { uid, r } = x;
    if (openUid === uid) {
      setOpenUid(null);
      return;
    }
    setOpenUid(uid);
    setDetailLoading(uid);
    try {
      // Kit rows: fetch the USPS scan timeline once (thin audit; fail-soft to a "no detail" note).
      if (r.kind === 'kit' && scans[uid] === undefined) {
        if (!r.barcode_id) {
          setScans((prev) => ({ ...prev, [uid]: 'none' }));
        } else {
          try {
            const d = await shippingDetail(r.barcode_id);
            setScans((prev) => ({ ...prev, [uid]: d.available && d.scans.length ? d.scans : 'none' }));
          } catch {
            setScans((prev) => ({ ...prev, [uid]: 'none' }));
          }
        }
      }
      // Both kinds: the member's full transaction record below (reason-gated, cached per member).
      if (r.member_id && !memberDetails[r.member_id]) {
        const md = await memberDetail(r.member_id, sessionReason);
        if (md.ok && (md.data as MemberDetailResp).found) {
          setMemberDetails((prev) => ({ ...prev, [r.member_id]: md.data as MemberDetailResp }));
        } else {
          setNote(`Full record unavailable for #${fmtMemberId(r.member_id)} (HTTP ${md.status}).`);
        }
      }
    } finally {
      setDetailLoading('');
    }
  };

  const runExport = async (exportReason: string) => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await shippingListExport(exportReason, kind, sorted.map((x) => shippingRowKey(x.r)));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!reasonValid) {
    return (
      <div className="note" style={{ marginBottom: 14 }}>
        Provide a reason-for-access to load the {noun} (rows carry member identity and the read is audited with your reason).{' '}
        {onNeedReason && <button type="button" className="btn btn-sm" onClick={onNeedReason}>Provide reason</button>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <BrowseToolbar
        search={search}
        onSearch={setSearch}
        placeholder={kind === 'letter' ? 'Member ID, name, letter id, tracking #' : 'Tracking # / kit barcode / member ID / name'}
        ariaLabel={`Search ${noun}`}
        shown={sorted.length}
        total={rows.length}
        noun={noun}
        right={onRequireExportReason ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={exporting || sorted.length === 0}
            title={`Export the filtered ${noun} (current view) to Excel`}
            onClick={() => {
              setExportErr('');
              onRequireExportReason((exportReason) => { void runExport(exportReason); });
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        ) : undefined}
      />
      {exportErr && <div className="note danger" style={{ marginBottom: 14 }}>{exportErr}</div>}

      {note && (
        <div className="note" style={{ marginBottom: 14 }}>
          {note}
        </div>
      )}
      {loadErr && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          {loadErr}
        </div>
      )}
      {!loading && !loadErr && emptyReason && EMPTY_REASON_META[emptyReason].notice && (
        <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
          {EMPTY_REASON_META[emptyReason].notice}
        </div>
      )}
      {!available && !loading && !loadErr && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          No shipping tables in this campaign DB.
        </div>
      )}
      {truncated && (
        <div className="muted small" style={{ marginBottom: 8 }}>
          The population hit the server cap - this list is partial (search still covers what loaded).
        </div>
      )}

      <GridFrame view={view}>
      <GridToolbar view={view} />
      <div ref={virtual.scrollRef} className="card card-flush" style={{ maxHeight: view.expanded ? 'calc(100vh - 90px)' : '65vh', overflow: 'auto' }}>
        <table className="dt">
          <thead>
            <GridHeadRow view={view} />
          </thead>
          <tbody>
            {displayItems.length === 0 && (
              <tr>
                <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                  {loading ? `Loading ${noun}...` : emptyRow(emptyReason, search.trim() ? `No ${noun} match.` : `No ${noun}.`)}
                </td>
              </tr>
            )}
            <SpacerRow height={virtual.padTop} colSpan={span} />
            {virtual.items.map((vi) => {
              const item = displayItems[vi.index];
              if (!item) return null;
              const { uid, r } = item.row;
              if (item.kind === 'detail') {
                const md = r.member_id ? memberDetails[r.member_id] : undefined;
                const rowScans = scans[uid];
                return (
                  <tr key={vi.key} data-index={vi.index} ref={virtual.measureRef}>
                    <td colSpan={span} style={{ background: 'var(--paper-2)', padding: '12px 16px', height: 'auto' }}>
                      {r.kind === 'kit' && (
                        <div style={{ marginBottom: md ? 14 : 0 }}>
                          <div style={{ fontWeight: 600 }}>USPS scans {r.barcode_id && <span className="muted">kit {r.barcode_id}</span>}</div>
                          {rowScans === undefined && <div className="muted small" style={{ padding: '4px 0' }}>loading...</div>}
                          {rowScans === 'none' && (
                            <div className="muted small" style={{ padding: '4px 0' }}>
                              No per-scan detail in this campaign DB (summary statuses above are current).
                            </div>
                          )}
                          {Array.isArray(rowScans) && (
                            <div style={{ overflowX: 'auto', marginTop: 4 }}>
                              <table className="dt">
                                <thead>
                                  <tr>
                                    <th>When</th>
                                    <th>Direction</th>
                                    <th>Event</th>
                                    <th>Where</th>
                                    <th>Tracking</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rowScans.map((s, j) => (
                                    <tr key={`${s.event_ts}|${s.event_code}|${j}`}>
                                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{cell(s.event_ts)}</td>
                                      <td className="small">{cell(s.direction)}</td>
                                      <td className="small">{cell(s.event_type || s.event_code)}</td>
                                      <td className="small">{cell([s.city, s.state, s.zip].filter(Boolean).join(', '))}</td>
                                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{cell(s.tracking_number)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                      {r.kind === 'letter' && (
                        <div className="small" style={{ marginBottom: md ? 14 : 0 }}>
                          <span style={{ fontWeight: 600 }}>Letter</span>{' '}
                          {[
                            r.letter_id && `id ${r.letter_id}`,
                            r.sent_date && `sent ${r.sent_date}`,
                            r.tracking_code && `tracking ${r.tracking_code}`,
                            r.carrier && r.carrier,
                            r.delivery_status && r.delivery_status,
                            r.delivered_ts && `delivered ${r.delivered_ts}`,
                          ]
                            .filter(Boolean)
                            .join(' · ') || <span className="muted">no further letter detail</span>}
                        </div>
                      )}
                      {r.member_id && !md && detailLoading !== uid && (
                        <div className="muted small">Full record unavailable.</div>
                      )}
                      {r.member_id && !md && detailLoading === uid && (
                        <div className="muted small">Loading the full record...</div>
                      )}
                      {md && <MemberDetailPanel detail={md} />}
                    </td>
                  </tr>
                );
              }
              const expanded = openUid === uid;
              return (
                <tr
                  key={vi.key}
                  data-index={vi.index}
                  ref={virtual.measureRef}
                  tabIndex={0}
                  role="button"
                  aria-expanded={expanded}
                  aria-label={`Open shipment detail for ${r.full_name || 'member'}`}
                  onClick={() => void toggleRow(item.row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void toggleRow(item.row);
                    }
                  }}
                  style={{ cursor: 'pointer', background: expanded ? 'var(--paper-2)' : undefined }}
                >
                  {view.visible.map((c) => (
                    <td key={c.key} {...gridCell(view, c, item.row)}>
                      {c.render(item.row, vi.index)}
                    </td>
                  ))}
                </tr>
              );
            })}
            <SpacerRow height={virtual.padBottom} colSpan={span} />
          </tbody>
        </table>
      </div>
      </GridFrame>
      </div>
      <SlicerRail
        axes={axes}
        filters={filters}
        onFilters={setFilters}
        scope={{ shown: railFiltered.length, total: rows.length }}
        noun={noun}
      />
    </div>
  );
}
